import React from 'react';
import { Layout, MessageSquare, Radio, Users, Info, Settings, ChevronRight, PlusCircle, Bell } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { api } from '../api';

import { useAuth } from '../context/AuthContext';
import { useActivities } from '../context/ActivitiesContext';
import { canManageCommunity } from '../utils/permissions';

const CommunitySidebarItem = ({ icon: Icon, label, to, active }) => (
  <Link to={to} className={cn(
    "flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 group",
    active ? "bg-brand-indigo/10 text-brand-indigo shadow-sm" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
  )}>
    <div className={cn(
      "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300",
      active ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "bg-gray-50 text-gray-400 group-hover:bg-white group-hover:text-gray-600 group-hover:shadow-sm"
    )}>
      <Icon size={18} className={cn("transition-transform duration-300", active ? "scale-110" : "group-hover:scale-110")} />
    </div>
    <span className={cn(
      "font-bold text-sm tracking-tight transition-colors duration-300",
      active ? "text-brand-indigo" : "text-gray-600 group-hover:text-gray-900"
    )}>
      {label}
    </span>
  </Link>
);

const CommunitySidebar = ({ communityId, isMobile, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { communities } = useActivities();
  const [localCommunity, setLocalCommunity] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  // Close sidebar on navigation if on mobile
  React.useEffect(() => {
    if (isMobile && onClose) {
      onClose();
    }
  }, [location.pathname]);

  const community = localCommunity || (communities && Array.isArray(communities) ? communities.find(c => String(c.id) === String(communityId) || c.slug === communityId) : null);
  const isCommunityAdmin = canManageCommunity(user, communityId, community);

  React.useEffect(() => {
    if (!community && communityId && !loading && !localCommunity) {
      const fetchCommunity = async () => {
        try {
          setLoading(true);
          const data = await api.get(`/communities/${communityId}`);
          setLocalCommunity(data);
        } catch (err) {
          console.error('Failed to fetch community for sidebar', err);
        } finally {
          setLoading(false);
        }
      };
      fetchCommunity();
    }
  }, [communityId, community, loading, localCommunity]);

  if (!communityId) return null;

  const menuItems = [
    { icon: Layout, label: 'Overview', to: `/c/${communityId}` },
    { icon: MessageSquare, label: 'Discussions', to: `/c/${communityId}/d` },
    { icon: Radio, label: 'Live Sessions', to: `/c/${communityId}/s` },
    { icon: Users, label: 'Members', to: `/c/${communityId}/m` },
    { icon: Info, label: 'Resources', to: `/c/${communityId}/r` },
  ];

  if (!community) return (
    <aside className="w-72 h-screen bg-white border-r border-gray-100 flex flex-col items-center justify-center p-8 pt-24">
      <div className="w-8 h-8 border-4 border-brand-indigo/10 border-t-brand-indigo rounded-full animate-spin"></div>
    </aside>
  );

  return (
    <aside className={cn(
      "h-screen bg-white flex flex-col pb-8 overflow-y-auto no-scrollbar",
      isMobile ? "w-full pt-8" : "w-72 border-r border-gray-100 pt-24"
    )}>
      {isMobile && (
        <div className="px-6 mb-6 flex justify-end">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl text-gray-400">
            <ChevronRight size={24} className="rotate-180" />
          </button>
        </div>
      )}
      <div className="px-6 mb-10">
        <Link to="/communities" className="flex items-center gap-2 text-gray-400 hover:text-brand-indigo mb-6 group transition-colors">
          <div className="p-1.5 bg-gray-50 rounded-lg group-hover:bg-brand-indigo/5">
            <ChevronRight size={14} className="rotate-180 group-hover:-translate-x-1 transition-transform" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Back to Hubs</span>
        </Link>
        <div className="flex items-center gap-4">
           <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-xl shadow-brand-indigo/10 overflow-hidden border-4 border-white shrink-0", !community.icon && "bg-brand-indigo")}>
              {community.icon ? (
                <img src={community.icon} alt="" className="w-full h-full object-cover" />
              ) : (
                community.name.substring(0, 2).toUpperCase()
              )}
           </div>
           <div className="min-w-0">
              <h2 className="font-bold text-gray-900 leading-tight truncate tracking-tight">{community.name}</h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mt-1">{community.privacy || 'Public'} Community</p>
           </div>
        </div>
      </div>

      <div className="px-3 space-y-1 mb-10">
        {menuItems.map((item) => (
          <CommunitySidebarItem 
            key={item.label} 
            {...item} 
            active={item.to === `/c/${communityId}` ? location.pathname === item.to : location.pathname.startsWith(item.to)}
          />
        ))}
      </div>

      <div className="px-6 mt-auto">
         {isCommunityAdmin && (
           <div className="bg-gray-50/50 rounded-3xl p-5 mb-6 border border-gray-100">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-4">Moderation</h4>
              <button 
                onClick={() => navigate(`/c/${communityId}/settings`)}
                className="w-full flex items-center justify-between text-gray-600 hover:text-brand-indigo transition-colors group"
              >
                 <span className="text-xs font-bold">Manage Hub</span>
                 <Settings size={14} className="group-hover:rotate-45 transition-transform" />
              </button>
           </div>
         )}
         <button 
            onClick={() => navigate(`/c/${communityId}/d/new`)}
            className="w-full bg-brand-indigo hover:bg-brand-indigo/90 text-white font-bold py-4 px-4 rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 shadow-xl shadow-brand-indigo/20 active:scale-[0.96] group overflow-hidden relative text-sm"
         >
            <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            <PlusCircle size={22} className="relative z-10 group-hover:rotate-12 transition-transform duration-300" />
            <span className="relative z-10 tracking-tight">New Post</span>
         </button>
      </div>
    </aside>
  );
};

export default CommunitySidebar;
